import { JwtInterceptor } from './auth/jwt-interceptor';
import { APP_INITIALIZER, ErrorHandler, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { DatePipe, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { BrowserModule, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';

// Import routing module
import { AppRoutingModule } from './app-routing.module';

// Import app component
import { AppComponent } from './app.component';

import { OAuthModule, OAuthService, OAuthModuleConfig  } from 'angular-oauth2-oidc';

// Import containers
import { DefaultFooterComponent, DefaultHeaderComponent, DefaultLayoutComponent } from './containers';
import { ErrorHandlerService } from './services/error-handler.service';
import { ErrorComponent } from './views/error/error.component';
import { GlobalErrorInterceptor } from './interceptors/global-error.interceptor';

import {
  AvatarModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonGroupModule,
  ButtonModule,
  CardModule,
  DropdownModule,
  FooterModule,
  FormModule,
  GridModule,
  HeaderModule,
  ListGroupModule,
  NavModule,
  ProgressModule,
  SharedModule,
  SidebarModule,
  TabsModule,
  UtilitiesModule
} from '@coreui/angular';

import { IconModule, IconSetService } from '@coreui/icons-angular';
import {AuthService} from "./auth.service";
import {Store, StoreModule} from "@ngrx/store";
import { environment } from "../environments/environment";
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as fromRoot from './state/app.reducers';
import {ConfigurationService} from "./services/configuration.service";
import {ToastModule} from "primeng/toast";
import {MessageService} from "primeng/api";
import { FacilityService } from './services/facility.service';
import { firstValueFrom } from 'rxjs';
import { addCorporations } from './views/rpm/state/rpm-page.actions';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule as PrimeButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { BadgeModule as PrimeBadgeModule } from 'primeng/badge';
import { SidebarModule as PrimeSidebarModule } from 'primeng/sidebar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {TitleStrategy} from "@angular/router";
import {PortalTitleStrategy} from "./services/portal-title-strategy.service";
import { AnnouncementsDialogComponent } from './views/shared/announcements-dialog/announcements-dialog.component';
import { FormatDateService } from './services/formatdate.service';
import { SharedModule as AppSharedModule } from './views/shared/shared.module';

const APP_CONTAINERS = [
  DefaultFooterComponent,
  DefaultHeaderComponent,
  DefaultLayoutComponent
];

const APP_COMPONENTS = [
  ErrorComponent,
  AnnouncementsDialogComponent
];
export interface ExtendedOAuthModuleConfig extends OAuthModuleConfig {
  disableNonceCheck?: boolean;
}
const extendedOAuthModuleConfig: ExtendedOAuthModuleConfig = {
  resourceServer: {
    allowedUrls: [],
    sendAccessToken: false
  },
  // Other configuration options...
  disableNonceCheck: true
};

export function initializeApp(oauthService: OAuthService, authService: AuthService, configurationService: ConfigurationService, facilityService: FacilityService, store: Store): () => Promise<void> {
  return async () => {
    try {
      // Configure OAuth with Keycloak-specific optimizations
      oauthService.configure({
        issuer: environment.issuer,
        redirectUri: environment.redirectUri,
        clientId: environment.clientId,
        responseType: environment.responseType,
        scope: environment.scope,
        showDebugInformation: environment.showDebugInformation,
        strictDiscoveryDocumentValidation: false,
        skipIssuerCheck: true, // Prevents redirect loops with Keycloak
        sessionChecksEnabled: true,
        clearHashAfterLogin: true, // Clear hash fragments to prevent redirect issues
        silentRefreshTimeout: 5000, // 5 second timeout
        timeoutFactor: 0.75, // Refresh at 75% of token lifetime
      });
      oauthService.setStorage(localStorage);

      // Load discovery document with timeout to prevent indefinite hanging
      await Promise.race([
        oauthService.loadDiscoveryDocumentAndLogin({
          onTokenReceived: (info) => {
            console.log('Token received, preventing further redirects');
          }
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('OAuth initialization timeout')), 10000)
        )
      ]);

      if(oauthService.hasValidAccessToken()) {
        oauthService.setupAutomaticSilentRefresh();

        // Load profile and corporations in parallel for faster initialization
        await Promise.all([
          authService.getProfile().catch(error => {
            // TOOD: Improve error handling
            console.error('Profile loading failed:', error);
          }),
          getCorporations()
        ]);
      } else {
        // No valid token after initialization, redirect to Keycloak login
        console.warn('No valid access token, redirecting to login');
        oauthService.initCodeFlow();
      }
    } catch (error) {
      console.error('App initialization failed:', error);

      // If initialization completely fails, attempt login redirect
      if (!oauthService.hasValidAccessToken()) {
        try {
          oauthService.initCodeFlow();
        } catch (redirectError) {
          console.error('Failed to redirect to login:', redirectError);
          // TODO: As a last resort, redirect to an error page
        }
      }
    }
  };

  async function getCorporations() {
    try {
      const res = await firstValueFrom(facilityService.getInfrastructure());
      const sortedCorporations = res.corporations?.sort((a, b) => a.name?.localeCompare(b.name));
      facilityService.buildInfrastructureTree(sortedCorporations);
      store.dispatch(addCorporations({ corporations: sortedCorporations }));
    } catch (error) {
      console.error('Failed to load corporations:', error);
      // TOOD: Improve error handling
    }
  }
}

@NgModule({
  declarations: [AppComponent, ...APP_CONTAINERS, ...APP_COMPONENTS],
  imports: [
    BrowserModule,
    RouterModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AvatarModule,
    BreadcrumbModule,
    FooterModule,
    DropdownModule,
    GridModule,
    HttpClientModule,
    HeaderModule,
    SidebarModule,
    IconModule,
    NavModule,
    ButtonModule,
    FormModule,
    UtilitiesModule,
    ButtonGroupModule,
    ReactiveFormsModule,
    SidebarModule,
    SharedModule,
    AppSharedModule,
    TabsModule,
    ListGroupModule,
    ProgressModule,
    BadgeModule,
    ListGroupModule,
    CardModule,
    NgScrollbarModule,
    ToastModule,
    TooltipModule,
    OAuthModule.forRoot(),
    PrimeButtonModule,
    DialogModule,
    PrimeBadgeModule,
    FormatDateService,
    PrimeSidebarModule,
    ProgressSpinnerModule,
    StoreModule.forRoot(fromRoot.reducers, {metaReducers: fromRoot.metaReducers}),
    StoreDevtoolsModule.instrument({maxAge: 25, logOnly: !environment.production}),
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [OAuthService, AuthService, ConfigurationService, FacilityService, Store],
      multi: true,
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    IconSetService,
    MessageService,
    Title,
    DatePipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
   },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalErrorInterceptor,
      multi: true,
    },
    { provide: TitleStrategy, useClass: PortalTitleStrategy },
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule {
}
